import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { Canvases } from '@air/api';
import { useChatSidebarStore } from '@air/feature-ag-ui-chat';
import { callAirApi } from '@air/utils-api';

import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { type CanvasViewedFrom } from '~/components/v2/store/shared/createWorkspaceRightPanelCanvasSlice';
import { CanvasQueryParams, getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { type CanvasesView } from '~/constants/canvasViews';
import { CANVAS_EXCLUDED_ROUTES, Routes } from '~/constants/routes';
import { useDiscardDraftCanvas } from '~/hooks/canvases/useDiscardDraftCanvas';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { CANVASES_LIST } from '~/swr-hooks/canvases/useListCanvases';
import { queryClient } from '~/swr-hooks/queryClient';

/**
 * State/actions hook for selected canvas and right panel UI controls.
 * URL synchronization is handled separately by useSelectedCanvasIdSync.
 */
export const useSelectedCanvasId = () => {
  const { pathname, push } = useRouter();
  const { currentWorkspace } = useCurrentWorkspace();

  const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);
  const selectedCanvasId = useWorkspaceRightPanelStore((state) => state.selectedCanvasId);
  const setSelectedCanvasId = useWorkspaceRightPanelStore((state) => state.setSelectedCanvasId);
  const setOpen = useWorkspaceRightPanelStore((state) => state.setOpen);
  const setFullscreen = useWorkspaceRightPanelStore((state) => state.setFullscreen);

  const recordView = useCallback(
    (canvasId: string) => {
      if (!currentWorkspace?.id) {
        return;
      }
      callAirApi(() => Canvases.view({ workspaceId: currentWorkspace.id, canvasId }))
        .then(() => {
          queryClient.invalidateQueries({ queryKey: [CANVASES_LIST] });
        })
        .catch(() => {});
    },
    [currentWorkspace?.id],
  );

  const selectCanvas = useCallback(
    (canvasId: string, options?: { fullscreen?: boolean; viewedFrom?: CanvasViewedFrom }) => {
      if (CANVAS_EXCLUDED_ROUTES.has(pathname)) {
        push({ pathname: Routes.canvases, query: { [CanvasQueryParams.canvasId]: canvasId } });
        useChatSidebarStore.getState().setOpen(false);
        recordView(canvasId);
        return;
      }

      setSelectedCanvasId(canvasId, options?.viewedFrom);
      setOpen(true);
      setFullscreen(options?.fullscreen ?? false);
      useChatSidebarStore.getState().setOpen(false);
      recordView(canvasId);
    },
    [pathname, push, setSelectedCanvasId, setOpen, setFullscreen, recordView],
  );

  const clearSelectedCanvas = useCallback(() => {
    setSelectedCanvasId(null);
    setFullscreen(false);
  }, [setSelectedCanvasId, setFullscreen]);

  const toggleFullscreen = useCallback(() => {
    if (!selectedCanvasId) {
      return;
    }
    const newFullscreen = !isFullscreen;
    setFullscreen(newFullscreen);
  }, [selectedCanvasId, isFullscreen, setFullscreen]);

  const openFullscreen = useCallback(() => {
    setFullscreen(true);
  }, [setFullscreen]);

  const discardDraftCanvas = useDiscardDraftCanvas();

  const closeCanvas = useCallback(() => {
    discardDraftCanvas();
    setOpen(false);
    clearSelectedCanvas();
  }, [discardDraftCanvas, setOpen, clearSelectedCanvas]);

  const exitFullscreen = useCallback(() => {
    setFullscreen(false);
  }, [setFullscreen]);

  const selectCanvasesView = useCallback(
    (view: CanvasesView) => {
      push({ pathname: Routes.canvases, query: { ...getCanvasQueryParams(), tab: view } });
    },
    [push],
  );

  return {
    selectedCanvasId,
    selectCanvas,
    clearSelectedCanvas,
    toggleFullscreen,
    openFullscreen,
    closeCanvas,
    exitFullscreen,
    selectCanvasesView,
  };
};
